import { useCallback, useEffect, useRef } from "react";

/**
 * Tiny Web Audio synthesiser for darkroom ambience.
 * - click(): shutter snap
 * - whisper(ms): Phantom radio whisper
 * - startAmbient() / stopAmbient(): low basement drone
 * Honours `enabled` reactive flag — silent when off.
 */
export default function useAudio(enabled) {
    const ctxRef = useRef(null);
    const ambientRef = useRef(null);

    const getCtx = useCallback(() => {
        if (typeof window === "undefined") return null;
        if (!ctxRef.current) {
            try {
                const Ctor = window.AudioContext || window.webkitAudioContext;
                if (!Ctor) return null;
                ctxRef.current = new Ctor();
            } catch (e) {
                console.warn("AudioContext construction failed:", e?.message || e);
                return null;
            }
        }
        if (ctxRef.current.state === "suspended") {
            try {
                ctxRef.current.resume();
            } catch (e) {
                console.warn("AudioContext resume failed:", e?.message || e);
            }
        }
        return ctxRef.current;
    }, []);

    const click = useCallback(() => {
        if (!enabled) return;
        const c = getCtx(); if (!c) return;
        const now = c.currentTime;
        // mechanical snap = noise burst through bandpass + brief sine ping
        const buf = c.createBuffer(1, Math.floor(c.sampleRate * 0.08), c.sampleRate);
        const data = buf.getChannelData(0);
        for (let i = 0; i < data.length; i++) {
            data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (c.sampleRate * 0.012));
        }
        const src = c.createBufferSource(); src.buffer = buf;
        const bp = c.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = 2200; bp.Q.value = 4.5;
        const g = c.createGain();
        g.gain.setValueAtTime(0.45, now);
        g.gain.exponentialRampToValueAtTime(0.0001, now + 0.08);
        src.connect(bp).connect(g).connect(c.destination);
        src.start(now);

        // tiny mechanical ping
        const osc = c.createOscillator();
        osc.type = "triangle"; osc.frequency.value = 1100;
        const og = c.createGain();
        og.gain.setValueAtTime(0.18, now);
        og.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);
        osc.connect(og).connect(c.destination);
        osc.start(now); osc.stop(now + 0.06);
    }, [enabled, getCtx]);

    const whisper = useCallback((durationMs = 1200) => {
        if (!enabled) return;
        const c = getCtx(); if (!c) return;
        const now = c.currentTime;
        const dur = durationMs / 1000;
        const sr = c.sampleRate;
        const buf = c.createBuffer(1, Math.floor(sr * dur), sr);
        const d = buf.getChannelData(0);
        for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * 0.6;
        const src = c.createBufferSource(); src.buffer = buf;
        const bp = c.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = 850; bp.Q.value = 12;
        const lfo = c.createOscillator(); lfo.frequency.value = 5.5;
        const lfoGain = c.createGain(); lfoGain.gain.value = 340;
        lfo.connect(lfoGain).connect(bp.frequency);
        const g = c.createGain();
        g.gain.setValueAtTime(0.0001, now);
        g.gain.linearRampToValueAtTime(0.16, now + 0.12);
        g.gain.linearRampToValueAtTime(0.0001, now + dur);
        src.connect(bp).connect(g).connect(c.destination);
        src.start(now); lfo.start(now);
        src.stop(now + dur); lfo.stop(now + dur);
    }, [enabled, getCtx]);

    const startAmbient = useCallback(() => {
        if (!enabled) return;
        const c = getCtx(); if (!c) return;
        if (ambientRef.current) return;
        const osc1 = c.createOscillator(); osc1.type = "sawtooth"; osc1.frequency.value = 54;
        const osc2 = c.createOscillator(); osc2.type = "sawtooth"; osc2.frequency.value = 57.3;
        const filt = c.createBiquadFilter(); filt.type = "lowpass"; filt.frequency.value = 230; filt.Q.value = 6;
        const g = c.createGain(); g.gain.value = 0.035;
        osc1.connect(filt); osc2.connect(filt); filt.connect(g).connect(c.destination);
        osc1.start(); osc2.start();
        // slow LFO sweep for "haunted basement" feel
        const sweep = c.createOscillator(); sweep.frequency.value = 0.07;
        const sweepGain = c.createGain(); sweepGain.gain.value = 90;
        sweep.connect(sweepGain).connect(filt.frequency);
        sweep.start();
        ambientRef.current = { osc1, osc2, sweep, gain: g };
    }, [enabled, getCtx]);

    const chime = useCallback(() => {
        if (!enabled) return;
        const c = getCtx(); if (!c) return;
        const now = c.currentTime;
        // CRT-style level-up sting: two overlapping sine notes (A5 → E6) with a
        // bandpass giving it a warm retro character. ~380ms total.
        const notes = [
            { freq: 880,  start: 0,     dur: 0.28, gain: 0.22 },
            { freq: 1320, start: 0.06,  dur: 0.30, gain: 0.18 },
        ];
        notes.forEach(({ freq, start, dur, gain }) => {
            const osc = c.createOscillator();
            osc.type = "sine";
            osc.frequency.value = freq;
            const bp = c.createBiquadFilter();
            bp.type = "bandpass"; bp.frequency.value = 2400; bp.Q.value = 1.2;
            const g = c.createGain();
            const t0 = now + start;
            g.gain.setValueAtTime(0.0001, t0);
            g.gain.linearRampToValueAtTime(gain, t0 + 0.012);
            g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
            osc.connect(bp).connect(g).connect(c.destination);
            osc.start(t0); osc.stop(t0 + dur + 0.02);
        });
    }, [enabled, getCtx]);

    const buzz = useCallback(() => {
        if (!enabled) return;
        const c = getCtx(); if (!c) return;
        const now = c.currentTime;
        // Discordant buzzer — two sawtooth notes a semitone apart (very dissonant)
        // through a notchy bandpass + a low sine thump for punch. ~280ms.
        const notes = [
            { freq: 220,   dur: 0.28, gain: 0.22 },
            { freq: 233.1, dur: 0.28, gain: 0.20 },   // minor 2nd — the classic "wrong" sound
        ];
        notes.forEach(({ freq, dur, gain }) => {
            const osc = c.createOscillator();
            osc.type = "sawtooth";
            osc.frequency.value = freq;
            const bp = c.createBiquadFilter();
            bp.type = "bandpass"; bp.frequency.value = 420; bp.Q.value = 3;
            const g = c.createGain();
            g.gain.setValueAtTime(0.0001, now);
            g.gain.linearRampToValueAtTime(gain, now + 0.008);
            g.gain.linearRampToValueAtTime(0.0001, now + dur);
            osc.connect(bp).connect(g).connect(c.destination);
            osc.start(now); osc.stop(now + dur + 0.02);
        });
        // Low bass thump for punch
        const bass = c.createOscillator();
        bass.type = "sine"; bass.frequency.value = 55;
        const bg = c.createGain();
        bg.gain.setValueAtTime(0.0001, now);
        bg.gain.linearRampToValueAtTime(0.28, now + 0.006);
        bg.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);
        bass.connect(bg).connect(c.destination);
        bass.start(now); bass.stop(now + 0.17);
    }, [enabled, getCtx]);

    const stopAmbient = useCallback(() => {
        const a = ambientRef.current;
        if (!a) return;
        try {
            a.osc1.stop(); a.osc2.stop(); a.sweep.stop();
        } catch (e) {
            // Already stopped or context closed — fine
            console.debug("Ambient nodes already stopped:", e?.message || e);
        }
        ambientRef.current = null;
    }, []);

    useEffect(() => {
        if (!enabled) stopAmbient();
        return () => stopAmbient();
    }, [enabled, stopAmbient]);

    return { click, whisper, chime, buzz, startAmbient, stopAmbient };
}
